/**
 * Build a clear snackbar message from wizard field errors.
 */

const humanizeKey = (key) => {
    if (!key) return 'This field';
    const leaf = String(key).split('.').pop();
    return leaf
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

const resolveFieldLabel = (errorKey, fieldsMap) => {
    if (!errorKey || !fieldsMap) return humanizeKey(errorKey);

    const parts = String(errorKey).split('.');
    let fields = fieldsMap;
    let label;

    for (let i = 0; i < parts.length; i += 1) {
        const part = parts[i];
        if (/^\d+$/.test(part)) {
            continue;
        }
        const field = fields?.[part];
        if (!field) break;
        label = field.label || humanizeKey(part);
        if (field.type === 'repeatable-form') {
            fields = field.fields;
        } else if (field.subFields) {
            // keep searching inside visible subFields if needed
            const nested = field.subFields.reduce((acc, sub) => ({ ...acc, ...(sub.fields || {}) }), {});
            fields = { ...fields, ...nested };
        }
    }

    return label || humanizeKey(errorKey);
};

/**
 * @param {Array<{key: string, message: string}>} errors
 * @param {object} options
 * @param {string} options.stepLabel - e.g. "Itineraries"
 * @param {object} options.fieldsMap - stepsConfig[step].fields
 */
export const formatValidationMessage = (errors, { stepLabel, fieldsMap } = {}) => {
    if (!errors?.length) {
        return `Please complete the required fields${stepLabel ? ` in ${stepLabel}` : ''} before continuing.`;
    }

    const first = errors[0];
    if (first?.message && /^Please add at least one/i.test(first.message)) {
        const extra = errors.length > 1
            ? ` (+${errors.length - 1} more issue${errors.length === 2 ? '' : 's'})`
            : '';
        return `${first.message}${extra}`;
    }

    const fieldLabel = resolveFieldLabel(first.key, fieldsMap);
    const detail = first.message || 'This field is required';
    const extra = errors.length > 1
        ? ` (+${errors.length - 1} more issue${errors.length === 2 ? '' : 's'})`
        : '';

    const stepPrefix = stepLabel ? `${stepLabel}: ` : '';
    return `${stepPrefix}${fieldLabel} — ${detail}${extra}`;
};

export default formatValidationMessage;
